// SPDX-License-Identifier: Elastic-2.0
/**
 * SignInForm — cloud email + password re-authentication (F-613).
 *
 * Rendered by `SignInPage` in non-fixture mode when an expired / signed-
 * out cloud user is bounced to `/onboarding/account` by `AuthGuard`.
 * Calls `POST /auth/login` on mirlai-accounts, commits the returned
 * session through `useAuth.setCloudSession` (which writes `mirlai_jwt` +
 * `mirlai_refresh` via `tokenStorage`), and returns the user to wherever
 * they came from.
 *
 * This is **re-auth only** — it does not create accounts or pair devices
 * (that stays in onboarding A5). The session-commit + user-mapping path
 * mirrors `usePairingFlow.ts` so both entry points populate the auth
 * store identically.
 *
 * The pure helpers (`validate`, `messageForError`, `resolveReturnTo`)
 * are exported for direct unit testing (see SignInForm.test.tsx).
 */
import { useRef, useState } from "react";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";

import { AuthService } from "@/api/accounts";
import type { TokenResponse } from "@/api/accounts";
import type { AuthUser } from "@/api/auth-store";
import { useAuth } from "@/api/auth-store";

import { ROUTES } from "../../routing/routes";
import {
  messageForError,
  resolveReturnTo,
  validate,
  type FieldErrors,
} from "./signInForm.helpers";
import styles from "./SignInForm.module.css";

/** Compact identity projection → the auth store's slim user shape. */
function toAuthUser(user: TokenResponse["user"]): AuthUser {
  return {
    id: user.user_id,
    email: user.email,
    display_name: user.display_name ?? "",
  };
}

export function SignInForm(): JSX.Element {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const setCloudSession = useAuth((s) => s.setCloudSession);

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const emailRef = useRef<HTMLInputElement>(null);
  const passwordRef = useRef<HTMLInputElement>(null);

  const reauth = searchParams.get("reauth") === "true";

  async function handleSubmit(e: React.FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    const errors = validate(email, password);
    if (errors.email || errors.password) {
      setFieldErrors(errors);
      setFormError(null);
      // Move focus to the first invalid field for keyboard / SR users.
      (errors.email ? emailRef : passwordRef).current?.focus();
      return;
    }

    setFieldErrors({});
    setFormError(null);
    setSubmitting(true);
    try {
      const res = await AuthService.loginAuthLoginPost({
        requestBody: { email: email.trim(), password },
      });
      setCloudSession(
        res.access_token,
        res.refresh_token,
        toAuthUser(res.user),
        res.user.tier,
      );
      // Leave `submitting` set through the navigation so the button
      // cannot be re-fired during the redirect.
      navigate(resolveReturnTo(searchParams, location), { replace: true });
    } catch (err) {
      setFormError(messageForError(err));
      setSubmitting(false);
    }
  }

  return (
    <form className={styles.form} onSubmit={handleSubmit} noValidate data-testid="signin-form">
      <h1 className={styles.title}>Sign in</h1>
      <p className={styles.intro}>
        {reauth
          ? "Your session expired. Sign in to pick up where you left off."
          : "Sign in to your Mirlai account."}
      </p>

      {formError !== null && (
        <p className={styles.formError} role="alert" data-testid="signin-error">
          {formError}
        </p>
      )}

      <label className={styles.field}>
        <span className={styles.label}>Email</span>
        <input
          ref={emailRef}
          className={styles.input}
          type="email"
          name="email"
          autoComplete="email"
          autoFocus
          value={email}
          disabled={submitting}
          aria-invalid={fieldErrors.email ? true : undefined}
          aria-describedby={fieldErrors.email ? "signin-email-error" : undefined}
          onChange={(e) => {
            setEmail(e.target.value);
            if (fieldErrors.email) {
              setFieldErrors((prev) => ({ ...prev, email: undefined }));
            }
          }}
        />
        {fieldErrors.email && (
          <span id="signin-email-error" className={styles.fieldError}>
            {fieldErrors.email}
          </span>
        )}
      </label>

      <label className={styles.field}>
        <span className={styles.label}>Password</span>
        <input
          ref={passwordRef}
          className={styles.input}
          type="password"
          name="password"
          autoComplete="current-password"
          value={password}
          disabled={submitting}
          aria-invalid={fieldErrors.password ? true : undefined}
          aria-describedby={fieldErrors.password ? "signin-password-error" : undefined}
          onChange={(e) => {
            setPassword(e.target.value);
            if (fieldErrors.password) {
              setFieldErrors((prev) => ({ ...prev, password: undefined }));
            }
          }}
        />
        {fieldErrors.password && (
          <span id="signin-password-error" className={styles.fieldError}>
            {fieldErrors.password}
          </span>
        )}
      </label>

      <button
        type="submit"
        className={styles.submit}
        disabled={submitting}
        aria-disabled={submitting || undefined}
        aria-busy={submitting || undefined}
        data-testid="signin-submit"
      >
        {submitting ? "Signing in…" : "Sign in"}
      </button>

      <Link className={styles.forgot} to={ROUTES.FORGOT_PASSWORD}>
        Forgot password?
      </Link>
    </form>
  );
}
